/* V2-CLEANUP
Delete this file when Search V2 Integration is complete.
This is used on Search for Transient, Monthly, Airport, and Event verticals
*/
import differenceWith from 'lodash/differenceWith';
import isUndefined from 'lodash/isUndefined';
import isEqual from 'lodash/isEqual';
import forEach from 'lodash/forEach';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {getWindow} from 'ssr-window';
import BrandUtils from '@spothero/utils/brand';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';

const window = getWindow();

class SHGoogleMap extends Component {
    static propTypes = {
        isAdmin: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        searchRequestId: PropTypes.string,
        markers: PropTypes.array,
        zoom: PropTypes.number,
        destination: PropTypes.shape({
            title: PropTypes.string.isRequired,
            position: PropTypes.any,
        }).isRequired,
        kmlMapLayerEnabled: PropTypes.bool,
        kmlMapLayerURL: PropTypes.string,
        mapOptions: PropTypes.object.isRequired,
        onDragStart: PropTypes.func,
        onDragEnd: PropTypes.func,
        onZoom: PropTypes.func,
        onGoogleMapLoaded: PropTypes.func,
        onMarkerClick: PropTypes.func,
        staticBaseUrl: PropTypes.string.isRequired,
    };
    static defaultProps = {
        markers: [],
        zoom: 14,
        onDragStart: noop,
        onDragEnd: noop,
        onZoom: noop,
        onGoogleMapLoaded: noop,
        onMarkerClick: noop,
    };

    constructor(props) {
        super(props);

        const {isAdmin, isSoldOutEnabled, staticBaseUrl} = props;
        const isBrowser = EnvironmentUtils.isBrowser();

        this._gmaps = isBrowser ? window.google.maps : null;
        this._zIndexModifiers = {};
        this._zIndexBySpot = {};
        this._disabledIconHover = isBrowser
            ? {
                  size: new this._gmaps.Size(39, 64),
                  scaledSize: new this._gmaps.Size(195, 150),
                  origin:
                      isSoldOutEnabled && !isAdmin
                          ? new this._gmaps.Point(156, 63)
                          : new this._gmaps.Point(156, 0),
                  labelOrigin: new this._gmaps.Point(20, 20),
                  anchor: new this._gmaps.Point(16, 54),
              }
            : null;
        this._MARKER_CONFIG = isBrowser
            ? {
                  disabled: {
                      default: {
                          icon:
                              isSoldOutEnabled && !isAdmin
                                  ? this._disabledIconHover
                                  : {
                                        size: new this._gmaps.Size(12, 15),
                                        scaledSize: new this._gmaps.Size(
                                            195,
                                            150
                                        ),
                                        origin: new this._gmaps.Point(156, 127),
                                        labelOrigin: new this._gmaps.Point(
                                            22,
                                            20
                                        ),
                                        anchor: new this._gmaps.Point(3, 20),
                                    },
                          label: '',
                      },
                      hover: {
                          icon: this._disabledIconHover,
                          label: {
                              color: BrandUtils.getColor('cement'),
                              fontSize: '12px',
                          },
                      },
                  },
                  medium: {
                      default: {
                          icon: {
                              size: new this._gmaps.Size(45, 62),
                              scaledSize: new this._gmaps.Size(195, 150),
                              origin: new this._gmaps.Point(0, 88),
                              labelOrigin: new this._gmaps.Point(22, 20),
                              anchor: new this._gmaps.Point(19, 55),
                          },
                          label: {
                              color: BrandUtils.getColor('shift'),
                              fontSize: '12px',
                          },
                      },
                      hover: {
                          icon: {
                              size: new this._gmaps.Size(45, 71),
                              scaledSize: new this._gmaps.Size(195, 150),
                              origin: new this._gmaps.Point(110, 0),
                              labelOrigin: new this._gmaps.Point(22, 22),
                              anchor: new this._gmaps.Point(19, 61),
                          },
                          label: {
                              color: BrandUtils.getColor('go'),
                              fontSize: '13px',
                          },
                      },
                  },
                  large: {
                      default: {
                          icon: {
                              size: new this._gmaps.Size(49, 79),
                              scaledSize: new this._gmaps.Size(195, 150),
                              origin: new this._gmaps.Point(58, 0),
                              labelOrigin: new this._gmaps.Point(24, 24),
                              anchor: new this._gmaps.Point(22, 67),
                          },
                          label: {
                              color: BrandUtils.getColor('go'),
                              fontSize: '14px',
                          },
                      },
                      hover: {
                          icon: {
                              size: new this._gmaps.Size(56, 86),
                              scaledSize: new this._gmaps.Size(195, 150),
                              origin: new this._gmaps.Point(0, 0),
                              labelOrigin: new this._gmaps.Point(27, 27),
                              anchor: new this._gmaps.Point(25, 76),
                          },
                          label: {
                              color: BrandUtils.getColor('go'),
                              fontSize: '15px',
                          },
                      },
                  },
              }
            : null;
        this._markers = {};
        this._MARKER_SPRITE_PATH = `${staticBaseUrl}img/sprites/sprite-markers@2x.png`;
        this._HOVER_Z_INDEX = 999999;
        this._zoomThrottle = throttle(this._onZoomChanged, 1000);
    }

    componentDidMount() {
        const {
            zoom,
            destination,
            kmlMapLayerEnabled,
            kmlMapLayerURL,
            mapOptions,
            onDragStart,
            onDragEnd,
            onGoogleMapLoaded,
        } = this.props;

        this._map = new this._gmaps.Map(this._mapContainer, {
            center: destination.position,
            zoom,
            ...mapOptions,
        });

        if (destination && !kmlMapLayerEnabled) {
            this._destination = new this._gmaps.Marker({
                position: destination.position,
                map: this._map,
                title: destination.title,
            });
        }

        if (kmlMapLayerEnabled && kmlMapLayerURL) {
            this._addKML(kmlMapLayerURL);
        }

        this._gmaps.event.addListenerOnce(this._map, 'idle', () => {
            onGoogleMapLoaded(this._map);
        });
        this._gmaps.event.addListenerOnce(this._map, 'tilesloaded', () => {
            // don't add listeners until after tiles have loaded to avoid calling errant zoom changes from
            // underlying calls to fitBounds
            this._gmaps.event.addListener(this._map, 'dragstart', onDragStart);
            this._gmaps.event.addListener(this._map, 'dragend', onDragEnd);
            this._gmaps.event.addListener(
                this._map,
                'zoom_changed',
                this._zoomThrottle
            );
        });

        document.addEventListener('spotHover', this._onSpotHover);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            searchRequestId,
            markers,
            destination: {title: destinationTitle, position},
            zoom,
            kmlMapLayerEnabled,
            kmlMapLayerURL,
        } = this.props;
        const {
            destination: {position: prevPosition},
            searchRequestId: prevSearchRequestId,
            markers: prevMarkers,
            zoom: prevZoom,
            kmlMapLayerEnabled: prevKmlMapLayerEnabled,
            kmlMapLayerURL: prevKmlMapLayerURL,
        } = prevProps;

        if (
            searchRequestId !== prevSearchRequestId ||
            position !== prevPosition
        ) {
            this._destination.setPosition(position);
            this._destination.setOptions({
                map: this._map,
                title: destinationTitle,
            });
        }

        if (markers.length !== prevMarkers.length) {
            if (prevMarkers.length < markers.length) {
                forEach(
                    differenceWith(markers, prevMarkers, isEqual),
                    marker => {
                        this._addMarker(marker);
                    }
                );
            } else {
                forEach(
                    differenceWith(prevMarkers, markers, isEqual),
                    marker => {
                        this._removeMarker(marker);
                    }
                );
            }
        }

        if (zoom !== prevZoom) {
            this._map.setZoom(zoom);
        }

        if (
            (kmlMapLayerEnabled && !prevKmlMapLayerEnabled) ||
            (kmlMapLayerURL && !prevKmlMapLayerURL)
        ) {
            this._addKML(kmlMapLayerURL);
        }
    }

    componentWillUnmount() {
        this._gmaps.event.clearListeners(this._map, 'dragstart');
        this._gmaps.event.clearListeners(this._map, 'dragend');
        this._gmaps.event.clearListeners(this._map, 'zoom_changed');

        document.removeEventListener('spotHover', this._onSpotHover);

        this._removeAllMarkers();

        if (this._zoomThrottle) {
            this._zoomThrottle.cancel();
        }
    }

    _onZoomChanged = () => {
        // the 'zoom_changed' event gets fired in every zoom instance so we have to determine what to do with zoom levels in parent
        this.props.onZoom(this._map.getZoom());
    };

    _onSpotHover = evt => {
        const {
            detail: {hovered, latitude, spotId, activeMarker},
        } = evt;

        if (!this._markers[spotId]) {
            return;
        }

        const state = hovered ? 'hover' : 'default';
        const zIndex = hovered
            ? this._HOVER_Z_INDEX
            : activeMarker
            ? this._HOVER_Z_INDEX - 1
            : this._getZIndex({
                  spotId,
                  latitude,
              });
        const icon = {
            url: this._MARKER_SPRITE_PATH,
            ...this._MARKER_CONFIG[this._markers[spotId].markerType][state]
                .icon,
        };
        const label = this._markers[spotId].marker.getLabel();

        if (this._markers[spotId].markerType !== 'disabled') {
            label.color = this._MARKER_CONFIG[this._markers[spotId].markerType][
                state
            ].label.color;
            this._markers[spotId].marker.setLabel(label);
        }

        this._markers[spotId].marker.setIcon(icon);
        this._markers[spotId].marker.setZIndex(zIndex);
    };

    _addKML = kmlUrl => {
        if (!this._kml) {
            this._kml = new this._gmaps.KmlLayer({
                url: kmlUrl,
                map: this._map,
                preserveViewport: true,
            });
        }
    };

    _getZIndex = ({spotId, latitude}) => {
        if (!Object.prototype.hasOwnProperty.call(this._zIndexBySpot, spotId)) {
            const zIndex = Math.abs(latitude).toFixed(3) * 1000;

            if (
                Object.prototype.hasOwnProperty.call(
                    this._zIndexModifiers,
                    zIndex
                )
            ) {
                this._zIndexModifiers[zIndex] += 1;
            } else {
                this._zIndexModifiers[zIndex] = 0;
            }

            this._zIndexBySpot[spotId] = zIndex + this._zIndexModifiers[zIndex];
        }

        return this._zIndexBySpot[spotId];
    };

    _addMarker = marker => {
        const {isAdmin, onMarkerClick} = this.props;
        const {
            spotId,
            latitude,
            longitude,
            title,
            markerLabelText,
            markerType,
            state,
        } = marker;

        if (!isUndefined(this._markers[spotId])) {
            return this._markers[spotId];
        }

        const icon = {
            url: this._MARKER_SPRITE_PATH,
            ...this._MARKER_CONFIG[markerType][state].icon,
        };
        const zIndex =
            state === 'hover'
                ? this._HOVER_Z_INDEX
                : this._getZIndex({
                      spotId,
                      latitude,
                  });
        const labelFontSize =
            markerType !== 'disabled'
                ? markerLabelText.length > 5
                    ? `${
                          parseInt(
                              this._MARKER_CONFIG[markerType][
                                  state
                              ].label.fontSize.replace('px', ''),
                              10
                          ) - 2
                      }px`
                    : this._MARKER_CONFIG[markerType][state].label.fontSize
                : '12px';

        this._markers[spotId] = {
            marker: new this._gmaps.Marker({
                position: new this._gmaps.LatLng(latitude, longitude),
                map: this._map,
                label:
                    markerType === 'disabled' && state === 'default'
                        ? ''
                        : {
                              text: markerLabelText,
                              color: this._MARKER_CONFIG[markerType][state]
                                  .label.color,
                              fontSize: labelFontSize,
                              fontWeight: 'bold',
                              fontFamily: 'Open Sans,sans-serif',
                          },
                icon,
                title,
                zIndex,
            }),
            markerType,
            spotId,
            latitude,
        };
        this._markers[spotId].marker.addListener('click', () => {
            if (!isAdmin && markerType === 'disabled') {
                return;
            }

            onMarkerClick(spotId);
        });
        this._markers[spotId].marker.addListener('mouseover', () => {
            DOMUtils.sendCustomEvent('spotHover', {
                hovered: true,
                spotId,
                latitude,
            });
        });
        this._markers[spotId].marker.addListener('mouseout', () => {
            DOMUtils.sendCustomEvent('spotHover', {
                hovered: false,
                spotId,
                latitude,
            });
        });

        return this._markers[spotId];
    };

    _removeMarker = ({spotId}) => {
        if (!isUndefined(this._markers[spotId])) {
            this._gmaps.event.clearListeners(
                this._markers[spotId].marker,
                'click'
            );
            this._gmaps.event.clearListeners(
                this._markers[spotId].marker,
                'mouseover'
            );
            this._gmaps.event.clearListeners(
                this._markers[spotId].marker,
                'mouseout'
            );
            this._markers[spotId].marker.setMap(null);
            delete this._markers[spotId];
        }
    };

    _removeAllMarkers = () => {
        forEach(this._markers, marker => {
            this._removeMarker(marker);
        });
    };

    _toggleActiveMarker = (spotId, latitude, active = true) => {
        if (
            !this._markers[spotId] ||
            this._markers[spotId].markerType === 'disabled'
        ) {
            return;
        }

        const markerType = active ? 'large' : 'medium';
        const icon = {
            url: this._MARKER_SPRITE_PATH,
            ...this._MARKER_CONFIG[markerType].default.icon,
        };
        const label = this._markers[spotId].marker.getLabel();

        label.color = this._MARKER_CONFIG[markerType].default.label.color;
        this._markers[spotId].marker.setLabel(label);
        this._markers[spotId].marker.setIcon(icon);

        if (active) {
            this._markers[spotId].marker.setZIndex(this._HOVER_Z_INDEX - 1);
        } else {
            this._markers[spotId].marker.setZIndex(this._zIndexBySpot[spotId]);
        }

        this._markers[spotId].markerType = markerType;

        this._gmaps.event.clearListeners(
            this._markers[spotId].marker,
            'mouseover'
        );
        this._gmaps.event.clearListeners(
            this._markers[spotId].marker,
            'mouseout'
        );

        this._markers[spotId].marker.addListener('mouseover', () => {
            this._onSpotHover({
                detail: {
                    hovered: true,
                    spotId,
                    latitude,
                    activeMarker: active,
                },
            });
            DOMUtils.sendCustomEvent('spotHover', {
                hovered: true,
                spotId,
                latitude,
                activeMarker: active,
            });
        });
        this._markers[spotId].marker.addListener('mouseout', () => {
            DOMUtils.sendCustomEvent('spotHover', {
                hovered: false,
                spotId,
                latitude,
                activeMarker: active,
            });
        });
    };

    render() {
        return (
            <div
                ref={node => {
                    this._mapContainer = node;
                }}
                className="SHGoogleMap"
            />
        );
    }
}

export default SHGoogleMap;
